<template>
    <div class="">

        <div class="tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-overflow-auto tw-bg-white ">
            <table class="tw-min-w-full tw-leading-normal tw-w-full">
                <thead class="">
                    <tr>
                        <th v-for="c in columns" :key="c.name" :class="c.classes"
                            class="tw-px-5 tw-text-[10px] last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-borderx tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
                            <div role="button" @click="loading ? null : onSortClick(c)" v-if="c.sortable"
                                class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer">
                                <p>{{ $t(c.label) }}</p>
                                <icon
                                    :class="[c.name == sorting.sortBy && (sorting.sortDirection == 'desc' ? '!tw-text-gray-800' : '!tw-text-gray-800 !tw-rotate-180')]"
                                    icon="ic:round-sort" class="tw-text-lg tw-text-gray-200" />
                            </div>
                            <p v-else>{{ $t(c.label) }}</p>
                        </th>
                    </tr>
                </thead>

                <tbody v-if="loading">
                    <tr class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50"
                        v-for="i in 10" :key="i">
                        <td v-for="c in columns" :key="c.name" class="tw-px-2 tw-py-3">
                            <div
                                class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white  tw-rounded tw-min-w-[30px] tw-animate-pulse">

                            </div>
                        </td>
                    </tr>
                </tbody>

                <tbody v-if="!loading && data.length">
                    <ProductsRow :filters="filters" v-for="(item, index) in data" :page="options.current_page"
                        :key="item" :item="item" :index="index" />
                </tbody>

                <tbody v-if="!loading && !data.length">
                    <tr
                        class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50">
                        <td :colspan="columns.length">
                            <p class="tw-p-2 tw-py-5 tw-text-center">
                                No data found
                            </p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end tw-mt-4">
            <button @click="onPrev" :disabled="options.current_page == 1"
                :class="[(options.current_page == 1) && 'tw-bg-gray-50 tw-cursor-not-allowed hover:!tw-border-gray-200 tw-text-gray-300 tw-border-gray-100 hover:!tw-bg-gray-50']"
                class="tw-w-[120px] tw-py-2 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-300 tw-rounded tw-bg-white tw-duration-200">{{ $t('buttons.previous') }}</button>

            <button @click="onNext" :disabled="options.current_page == options.last_page"
                :class="[(options.current_page == options.last_page) && 'tw-bg-gray-50 tw-cursor-not-allowed hover:!tw-border-gray-200 tw-text-gray-300 tw-border-gray-100 hover:!tw-bg-gray-50']"
                class="tw-w-[120px] tw-py-2 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-300 tw-rounded tw-bg-white tw-duration-200">{{ $t('buttons.next') }}</button>
        </div>
    </div>

</template>


<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import ProductsRow from './ProductsRow.vue';

const emit = defineEmits(['update:filters', 'update:options', 'update:sorting', 'next', 'previous', 'refresh']);

const props = defineProps({
    filters: {
        type: Object,
        default: () => ({})
    },
    options: {
        type: Object,
        default: () => ({})
    },
    search: {
        type: String,
        default: ''
    },
    sorting: {
        type: Object,
        default: () => ({})
    },
    loading: {
        type: Boolean,
        default: false
    },
    data: {
        type: Array,
        default: () => []
    }
});

const sorting = ref({
    sortBy: 'total_orders',
    sortDirection: 'asc'
});
props

let columns = [
    { name: 'name', label: 'fields.product-name', sortable: false, classes: ['tw-w-[260px]'] },
    { name: 'total_orders', label: 'common.orders', sortable: true, classes: ['tw-w-[80px] !tw-text-center'] },
    { name: 'total_confirmed_orders', label: 'common.confirmed', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'total_delivered_orders', label: 'common.delivered', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'total_delivered_quantity', label: 'fields.quantity', sortable: true, classes: ['tw-w-[80px] !tw-text-center'] },
    { name: 'total_turnover', label: 'pages.dashboard.kpis.total-turnover', sortable: true, classes: ['tw-w-[150px] !tw-text-center'] },
    { name: 'total_delivered_product_cost', label: 'pages.dashboard.kpis.product-cost', sortable: true, classes: ['tw-w-[100px] !tw-text-center'] },
    { name: 'shipping_cost', label: 'pages.dashboard.kpis.shipping-cost', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'total_spend', label: 'pages.dashboard.kpis.ads', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'variant_fees', label: 'common.variant-expenses', sortable: false, classes: ['tw-w-[100px] !tw-text-center'] },
    { name: 'cost_per_order', label: 'common.cost-per-order', sortable: false, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'profit_per_order', label: 'pages.dashboard.kpis.profit-per-order', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'total_profit', label: 'pages.dashboard.kpis.net-profit', sortable: true, classes: ['tw-w-[120px] !tw-text-center'] },
    { name: 'details', label: '', sortable: false, classes: ['tw-w-[50px] !tw-text-center'] },
]





const onSortClick = (column) => {
    if (column.sortable) {

        // Reset sorting object
        sorting.value = {
            ...sorting.value,
            sortBy: column.name
        };

        if (sorting.value.sortDirection == 'asc') {
            sorting.value.sortDirection = 'desc';
        } else {
            sorting.value.sortDirection = 'asc';
        }
    }

    emit('update:sorting', sorting.value);
    emit('refresh');
    
};



const onNext = () => {
    emit('next')
}

const onPrev = () => {
    emit('prev')
}

</script>

<style></style>